import { unsafeCSS, html } from "lit";
import { queryAssignedNodes, property, state } from "lit/decorators.js";
import { LeafElement } from "../leaf-element.js";
import { ValidationController } from "../../utilities/controllers/validation.js";
import { requiredValidator } from "../../utilities/validators.js";
import "../checkbox/checkbox.js";
import { LeafCheckbox } from "../checkbox/checkbox.component.js";
import "../../utilities/events.js";
import "../../utilities/i18n.js";
import "@shoelace-style/localize";
import "../../utilities/translations/en.js";
import "../../utilities/mixins/form-control.js";
const styles = '/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n:host {\n  display: block;\n}\n:host ::slotted(*:not(:first-child)) {\n  margin-top: var(--leaf-spacing-8);\n}\n:host ::slotted([slot=note]) {\n  display: block;\n  margin-top: var(--leaf-spacing-4);\n}\n\n:host([invalid]) ::slotted(leaf-checkbox) {\n  position: relative;\n}\n:host([invalid]) ::slotted(leaf-checkbox)::before {\n  border: var(--leaf-border-width-button-default) solid var(--leaf-color-border-error);\n  content: "";\n  border-radius: 3px;\n  height: var(--leaf-spacing-24);\n  position: absolute;\n  width: var(--leaf-spacing-24);\n}\n\n:host(:focus),\n:host(:focus-visible),\n:host(:focus-within) {\n  outline: 0;\n  outline: var(--leaf-border-width-button-default) solid var(--leaf-color-border-active-outline);\n}\n\n:host([invalid]:focus),\n:host([invalid]:focus-visible),\n:host([invalid]:focus-within) {\n  outline: 0;\n  outline: var(--leaf-border-width-button-default) solid var(--leaf-color-border-error);\n}';
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _validation, _onSlotchange, onSlotchange_fn, _onFocusout, onFocusout_fn, _onKeydown, onKeydown_fn;
let nextUniqueId = 0;
class LeafCheckgroup extends LeafElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _onSlotchange);
    __privateAdd(this, _onFocusout);
    __privateAdd(this, _onKeydown);
    this.internals = this.attachInternals();
    __privateAdd(this, _validation, new ValidationController(this, {
      validators: [requiredValidator],
      nativeTarget: () => this
    }));
    this.selectedIndex = -1;
  }
  get boxes() {
    var _a;
    return ((_a = this.defaultNodes) == null ? void 0 : _a.filter((node) => node instanceof LeafCheckbox)) || [];
  }
  get note() {
    return this.querySelector('[slot="note"]');
  }
  connectedCallback() {
    super.connectedCallback();
    __privateGet(this, _validation).validate(this.value ? this.value : void 0);
    if (!this.hasAttribute("tabindex")) {
      this.tabIndex = 0;
    }
    this.addEventListener("focusout", __privateMethod(this, _onFocusout, onFocusout_fn));
    this.addEventListener("keydown", __privateMethod(this, _onKeydown, onKeydown_fn));
  }
  async firstUpdated() {
    await this.updateComplete;
    const callback = () => {
      var _a;
      const value = (_a = this.boxes) == null ? void 0 : _a.map((box) => box.checked ? box.value : null).filter(Boolean).join(", ");
      this.value = value;
      __privateGet(this, _validation).validate(this.value ? this.value : void 0);
    };
    const observer = new MutationObserver(callback);
    observer.observe(this, {
      attributeFilter: ["checked"],
      attributeOldValue: true,
      subtree: true
    });
  }
  async updated() {
    await this.updateComplete;
    this.id || (this.id = `leaf-checkgroup-${nextUniqueId++}`);
    if (this.note) {
      this.note.id = `${this.id}-note`;
      this.setAttribute("aria-describedby", this.note.id);
    }
  }
  render() {
    return html`
      <div class="leaf-c-checkgroup__wrapper">
        <slot @slotchange=${__privateMethod(this, _onSlotchange, onSlotchange_fn)}></slot>
        <slot name="note"></slot>
      </div>
      ${__privateGet(this, _validation).render()}
    `;
  }
}
_validation = /* @__PURE__ */ new WeakMap();
_onSlotchange = /* @__PURE__ */ new WeakSet();
onSlotchange_fn = function() {
  if (typeof this.name === "string") {
    const name = this.name;
    this.boxes.forEach((box) => box.setAttribute("name", name));
  }
};
_onFocusout = /* @__PURE__ */ new WeakSet();
onFocusout_fn = function(event) {
  if (!event.relatedTarget || !this.boxes.includes(event.relatedTarget)) {
    __privateGet(this, _validation).validate(this.value);
    this.boxes.forEach((box) => box.removeAttribute("tabindex"));
  }
};
_onKeydown = /* @__PURE__ */ new WeakSet();
onKeydown_fn = function(event) {
  let newIndex = this.selectedIndex;
  if (event.key === "ArrowRight" || event.key === "ArrowDown") {
    newIndex = (this.selectedIndex + 1) % this.boxes.length;
  } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
    newIndex = (this.selectedIndex - 1 + this.boxes.length) % this.boxes.length;
  }
  if (newIndex !== this.selectedIndex) {
    this.selectedIndex = newIndex;
    this.boxes.forEach((box) => box.removeAttribute("tabindex"));
    this.boxes[this.selectedIndex].tabIndex = 0;
    this.boxes[this.selectedIndex].focus();
  }
};
LeafCheckgroup.styles = unsafeCSS(styles);
LeafCheckgroup.formAssociated = true;
__decorateClass([
  queryAssignedNodes()
], LeafCheckgroup.prototype, "defaultNodes", 2);
__decorateClass([
  property({ attribute: false })
], LeafCheckgroup.prototype, "validators", 2);
__decorateClass([
  property()
], LeafCheckgroup.prototype, "name", 2);
__decorateClass([
  property()
], LeafCheckgroup.prototype, "value", 2);
__decorateClass([
  state()
], LeafCheckgroup.prototype, "selectedIndex", 2);
export {
  LeafCheckgroup
};
//# sourceMappingURL=checkgroup.component.js.map
